import _isNil from 'lodash/isNil';
import React from 'react';

import { Candidate } from '../../firebase/firestore/documents/candidate';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { CurrentUserProfilePresenter } from '../../app/hooks/CurrentUserProfilePresenter';

import { useCandidateOnDemandInterviewWatcher } from './useCandidateOnDemandInterviewWatcher';

export enum CandidatesRenderedByEnum {
  CALENDAR_VIEW = 'CALENDAR_VIEW',
  USE_CALENDAR = 'USE_CALENDAR',
}
const EMPTY_ARRAY: string[] = [];

const getAuthorizedAccounts = (
  isOperator: boolean,
  accessibleAccounts: string[],
  currentUserProfile: CurrentUserProfilePresenter | undefined,
) => {
  if (_isNil(currentUserProfile)) return [];
  if (isOperator) {
    const newAuthorizedAccounts = [...accessibleAccounts];
    if (!newAuthorizedAccounts.includes(currentUserProfile.account)) {
      newAuthorizedAccounts.push(currentUserProfile.account);
    }
    return newAuthorizedAccounts;
  }
  return [currentUserProfile.account];
};

const useCandidates = (
  activeCurrentDateRange?: { fromDate: Date; toDate: Date },
  candidatesRenderedBy?: CandidatesRenderedByEnum,
): { candidates: Candidate[]; isLoading: boolean } => {
  const [isLoading, setIsLoading] = React.useState(false);
  const isOperator = useStoreState((s) => s.app.user?.operator?.enabled) || false;
  const accessibleAccountsFromStore = useStoreState((s) => s.app.user?.operator?.accessibleAccounts) || EMPTY_ARRAY;
  const defaultAccount = useStoreState((s) => s.app.user?.operator?.defaultAccount);
  const prevPayload = React.useRef<undefined | string>();

  const { currentUserProfile } = useCurrentUserProfile();

  const accountStore = useStoreState((s) => s.app.accounts);
  const groupEventEnabled = accountStore?.configuration?.groupEventEnabled;

  const accessibleAccounts = React.useMemo(() => {
    if (defaultAccount && !accessibleAccountsFromStore.includes(defaultAccount)) {
      return [...accessibleAccountsFromStore, defaultAccount];
    }
    return accessibleAccountsFromStore;
  }, [accessibleAccountsFromStore, defaultAccount]);

  const authorizedAccounts = React.useMemo(() => {
    if (candidatesRenderedBy === CandidatesRenderedByEnum.CALENDAR_VIEW) {
      if (currentUserProfile && groupEventEnabled) {
        return [currentUserProfile.account];
      }
      return EMPTY_ARRAY;
    }
    return getAuthorizedAccounts(isOperator, accessibleAccounts, currentUserProfile);
  }, [isOperator, accessibleAccounts, currentUserProfile, candidatesRenderedBy, groupEventEnabled]);

  const fetchCandidates = useStoreActions((state) => state.calendar.fetchCandidates);
  const [candidates, setCandidates] = React.useState<Candidate[]>([]);
  const fetchCandidatesHandler = React.useCallback(
    (reload = false) => {
      const payload = {
        from: activeCurrentDateRange?.fromDate || new Date(),
        to: activeCurrentDateRange?.toDate || new Date(),
        isCalendarBased: candidatesRenderedBy === CandidatesRenderedByEnum.USE_CALENDAR,
      };
      const payloadString = JSON.stringify(payload);
      // istanbul ignore next
      if (prevPayload.current === payloadString && !reload) return;
      prevPayload.current = payloadString;
      setIsLoading(true);
      fetchCandidates(payload)
        .then((res) => {
          const filteredCandidates = res.data.data.filter((candidate: Candidate) => {
            if (authorizedAccounts.includes(candidate.account)) {
              return true;
            }
            // Add other security rule checks here if needed
            return false;
          });
          setCandidates(filteredCandidates);
        })
        .catch(() => {
          setCandidates([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    // eslint-disable-next-line
    [activeCurrentDateRange, candidatesRenderedBy, fetchCandidates, authorizedAccounts],
  );

  React.useEffect(() => {
    if (authorizedAccounts.length) {
      fetchCandidatesHandler();
    }
  }, [authorizedAccounts, fetchCandidatesHandler]);

  useCandidateOnDemandInterviewWatcher({
    candidateIds: candidates.map((c) => c.id),
    onChange: () => {
      fetchCandidatesHandler(true);
    },
  });

  return { candidates, isLoading };
};
export default useCandidates;
